import React, { useEffect, useCallback, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { useFormik } from "formik";
import BusinessCustomersActions from "../../Redux/transition/customers/business-customer.reducer";
import { createCustomHandleApostropheChange, removeTrailingSpacesAndDashes } from "../../Components/CleanInputs";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import CustomToast from "../../Components/CustomToast";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import "../../Styles/pages/product.scss";
import "../../Styles/components/segment.scss";
import "../../Styles/components/tags.scss";

export const CreateAudienceDialog = ({ visible, onHide, businessId, onCreated }) => {
    const { t } = useTranslation("common");
    const [errors, setErrors] = useState({});
    const [toastTrigger, setToastTrigger] = useState(false);
    const [clickSave, setClickSave] = useState(false);

    const [toast, setToast] = useState({
        severity: "",
        summary: "",
        detail: "",
        life: 0,
    });

    const dispatch = useDispatch();
    const createAudience = useCallback(
        (audience) => dispatch(BusinessCustomersActions.createAudienceRequest(audience)),
        [dispatch]
    );

    const { createdAudience, creatingAudience, createAudienceError } = useSelector((state) => state.businessCustomers);

    const errorMessage = createAudienceError?.detail || createAudienceError?.message || t("business_customers.audience_create_failure");

    useEffect(() => {
        if (!creatingAudience && clickSave && createdAudience) {
            setToast({
                severity: "success",
                summary: t("success"),
                detail: t("business_customers.audience_create_success"),
                life: 3000,
            });
            setToastTrigger(true);
        }
        if (!creatingAudience && clickSave && createAudienceError) {
            setToast({
                severity: "error",
                summary: t("err"),
                detail: errorMessage,
                life: 4000,
            });
            setToastTrigger(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creatingAudience, clickSave, createdAudience, createAudienceError]);

    const formik = useFormik({
        initialValues: { name: "" },
        validate: (values) => {
            const errors = {};
            if (!values.name.trim()) {
                errors.name = t("business_customers.audience_name_hint");
            }
            return errors;
        },
        onSubmit: (data) => {
            setClickSave(true);
            const importName = removeTrailingSpacesAndDashes(data.name);
            createAudience({
                importName,
                importedOn: moment().toISOString(),
                business: { id: businessId },
            });
        },
    });

    useEffect(() => {
        if (!visible) {
            formik.resetForm();
            setClickSave(false);
            setErrors({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    const { values } = formik;
    const commonParams = [formik, setErrors, t];
    const customHandleChange = createCustomHandleApostropheChange(...commonParams);

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);

    const getFormErrorMessage = (name) =>
        isFormFieldValid(name) && clickSave && (
            <small className="p-error">{formik.errors[name]}</small>
        );

    const handleClose = () => {
        formik.resetForm();
        setClickSave(false);
        setErrors({});
        setToastTrigger(false);
        onHide();
    };

    return (
        <Dialog
            visible={visible}
            onHide={handleClose}
            header={t("business_customers.create_audience")}
            style={{ width: "350px" }}
        >
            <CustomToast
                {...toast}
                trigger={toastTrigger}
                handleActionsAfterToast={() => {
                    setToastTrigger(false);
                    setClickSave(false);
                    formik.resetForm();
                    onCreated?.(createdAudience);
                    onHide();
                }}
                handleActionsAfterErrorToast={() => {
                    setToastTrigger(false);
                    setClickSave(false);
                }}
            />

            <form onSubmit={formik.handleSubmit} className="p-fluid" style={{ paddingInline: "4px" }}>
                <div className="p-field p-mb-3 mt-4">
                    <label
                        id="name_label"
                        htmlFor="name"
                        className={classNames("product-form-label", { "": isFormFieldValid("name") })}
                    >
                        {t("common.name")}
                    </label>
                    <InputText
                        id="name"
                        name="name"
                        value={values.name}
                        onChange={customHandleChange}
                        style={{ width: "100%" }}
                        maxLength={100}
                    />
                    {getFormErrorMessage("name")}
                    {errors.name ? (
                        <p className="character_errors">{errors.name}</p>
                    ) : (
                        <p className="segment_hint">{t("business_customers.audience_name_hint")}</p>
                    )}
                </div>

                <div className="p-field p-mb-3 mt-4">
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "2rem" }}>
                        <Button
                            label={t("subscriber.cancel")}
                            className="mr-1"
                            icon="pi pi-times"
                            onClick={handleClose}
                            id="cancel-tag-button"
                            type="button"
                        />
                        <Button
                            type="submit"
                            label={t("products.save_product")}
                            className="p-mt-2 save-btns"
                            disabled={creatingAudience || !formik.values.name.trim() || clickSave}
                            loading={creatingAudience}
                        />
                    </div>
                </div>
            </form>
        </Dialog>
    );
};
